import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Zap,
  Cpu,
  ShieldCheck,
  Download,
  Infinity as InfinityIcon,
  ArrowRight,
  Check,
  Loader2,
  Mic,
  AudioLines,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { PLANS, UNLIMITED_NOTE } from "../lib/plans";

const FEATURES = [
  { icon: Zap, title: "Low Quality · Instant", body: "Your device's built-in voices speak instantly. Zero wait, zero setup — great for quick drafts." },
  { icon: Cpu, title: "High Quality · Neural", body: "The Kokoro 82M model runs right in your browser for rich, natural speech — with WAV export." },
  { icon: ShieldCheck, title: "100% Local & Private", body: "Nothing leaves your device. No servers process your text — it's generated entirely on your machine." },
  { icon: Download, title: "Unlimited WAV Exports", body: "Download crisp .wav files of anything you generate and use them wherever you like." },
];

export default function Home() {
  const navigate = useNavigate();
  const { user, isPaid, openAuth, openPricing, requestCheckout, openBillingPortal, logout } = useAuth();
  const [busyKey, setBusyKey] = useState(null);

  const scrollToPricing = () => {
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
  };

  const buy = async (key) => {
    setBusyKey(key);
    try {
      await requestCheckout(key);
      setBusyKey(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusyKey(null);
    }
  };

  const manageBilling = async () => {
    try {
      await openBillingPortal();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not open billing management");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-mono">
      {/* NAV */}
      <nav className="flex items-center justify-between px-4 md:px-8 py-4 border-b border-zinc-900 sticky top-0 z-30 bg-[#050505]/90 backdrop-blur">
        <Link to="/" className="flex items-center gap-3">
          <div className="h-7 w-7 bg-volt flex items-center justify-center">
            <Zap className="text-black" size={16} strokeWidth={3} />
          </div>
          <span className="hidden min-[370px]:inline font-display text-xl tracking-tight">LOCAL VOICE</span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          {!isPaid && (
            <button
              onClick={() => openPricing()}
              data-testid="home-upgrade-btn"
              className="hidden sm:inline px-3 py-1.5 border border-zinc-800 text-zinc-300 text-[11px] font-bold uppercase tracking-widest hover:bg-zinc-800 transition-colors duration-100"
            >
              Pricing
            </button>
          )}
          {user ? (
            <>
              {isPaid && (
                <button
                  onClick={manageBilling}
                  data-testid="home-billing-btn"
                  className="hidden md:inline px-3 py-1.5 border border-zinc-800 text-zinc-300 text-[11px] font-bold uppercase tracking-widest hover:bg-zinc-800 transition-colors duration-100"
                >
                  Billing
                </button>
              )}
              <button
                onClick={logout}
                data-testid="home-logout-btn"
                className="hidden sm:inline px-3 py-1.5 border border-zinc-800 text-zinc-300 text-[11px] font-bold uppercase tracking-widest hover:bg-zinc-800 transition-colors duration-100"
              >
                Logout
              </button>
            </>
          ) : (
            <button
              onClick={openAuth}
              data-testid="home-signin-btn"
              className="px-3 py-1.5 border border-zinc-700 text-white text-[11px] font-bold uppercase tracking-widest hover:bg-zinc-800 transition-colors duration-100"
            >
              Sign In
            </button>
          )}
          <button
            onClick={() => navigate("/studio")}
            data-testid="home-launch-btn"
            className="px-4 py-1.5 bg-volt text-black text-[11px] font-bold uppercase tracking-widest hover:bg-volt-hover transition-colors duration-100"
          >
            <span className="min-[370px]:hidden">Studio</span>
            <span className="hidden min-[370px]:inline">Launch Studio</span>
          </button>
        </div>
      </nav>

      {/* HERO */}
      <header className="relative overflow-hidden border-b border-zinc-900 signal-grid">
        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center px-6 py-20 md:py-28 lg:py-32">
          <div>
            <div className="inline-flex items-center gap-2 border border-volt/40 bg-[#0b0d06] px-3 py-1.5 mb-8 animate-fade-up">
              <Mic size={13} className="text-volt" />
              <span className="text-[11px] tracking-[0.25em] uppercase text-volt">Local · Private · Unlimited-Ready</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-6 animate-fade-up">
              TURN TEXT INTO
              <br />
              <span className="text-volt">STUNNING SPEECH.</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed mb-10 animate-fade-up" style={{ animationDelay: "0.1s" }}>
              Instant system voices or rich neural speech, generated on your device with downloadable WAV files.
              Your text stays yours.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 animate-fade-up" style={{ animationDelay: "0.2s" }}>
              <button
                onClick={() => navigate("/studio")}
                data-testid="home-start-btn"
                className="flex items-center justify-center gap-2 bg-volt text-black px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-volt-hover transition-colors duration-100"
              >
                <span className="sm:hidden">Start Free</span>
                <span className="hidden sm:inline">Start Creating — Free</span>
                <ArrowRight size={16} strokeWidth={2.5} />
              </button>
              <button
                onClick={scrollToPricing}
                data-testid="home-view-plans-btn"
                className="flex items-center justify-center gap-2 border border-zinc-700 text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-zinc-800 transition-colors duration-100"
              >
                View Plans
              </button>
            </div>
            <p className="text-[11px] text-zinc-600 mt-6 tracking-wider">
              Free tier: 30,000 high-quality + 50,000 low-quality characters. No card required.
            </p>
          </div>

          <div className="hidden lg:block border border-zinc-800 bg-[#090909] p-5 animate-fade-up" style={{ animationDelay: "0.15s" }} aria-hidden>
            <div className="flex items-center justify-between border-b border-zinc-800 pb-4 mb-8">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                <AudioLines size={14} className="text-volt" /> Neural signal
              </div>
              <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-volt">
                <span className="h-1.5 w-1.5 bg-volt animate-pulse" /> Local
              </span>
            </div>
            <div className="h-64 flex items-center gap-1 border-x border-zinc-900 px-5">
              {Array.from({ length: 44 }).map((_, index) => (
                <span
                  key={index}
                  className="signal-bar flex-1 bg-volt"
                  style={{
                    height: `${18 + ((index * 29) % 74)}%`,
                    animationDelay: `${index * -34}ms`,
                  }}
                />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-[1px] bg-zinc-800 mt-8 text-[10px] uppercase tracking-[0.2em]">
              <span className="bg-[#0d0d0d] p-3 text-zinc-500">82M params</span>
              <span className="bg-[#0d0d0d] p-3 text-zinc-500">WAV output</span>
              <span className="bg-[#0d0d0d] p-3 text-volt">On-device</span>
            </div>
          </div>
        </div>
      </header>

      {/* FEATURES */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[1px] bg-zinc-800 border border-zinc-800">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="bg-[#0d0d0d] p-7 flex flex-col">
                <Icon className="text-volt mb-5" size={26} strokeWidth={2} />
                <h3 className="text-sm uppercase tracking-widest text-white mb-3">{f.title}</h3>
                <p className="text-xs text-zinc-500 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* UNLIMITED BANNER */}
      <section className="max-w-6xl mx-auto px-6 pb-8">
        <div className="border border-volt bg-[#101400] p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-6 shadow-[0_0_50px_rgba(212,255,0,0.1)]">
          <InfinityIcon size={48} className="text-volt shrink-0" strokeWidth={1.5} />
          <div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-3">
              ONE UPGRADE. ZERO GENERATION LIMITS.
            </h2>
            <p className="text-sm text-zinc-300 leading-relaxed max-w-3xl">
              Here's the deal: <span className="text-volt font-bold">every paid plan gives you the exact same
              unlimited generation</span> — unlimited characters, both engines, unlimited WAV exports. {UNLIMITED_NOTE}
            </p>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-4">
          <div className="text-[11px] tracking-[0.3em] uppercase text-volt mb-3">Choose Your Access</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight">SAME UNLIMITED, YOUR DURATION</h2>
          <p className="text-sm text-zinc-500 mt-4 max-w-xl mx-auto">
            Choose the billing interval that works for you. The feature set is identical.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-14">
          {PLANS.map((p) => {
            const Icon = p.icon;
            const featured = p.featured;
            return (
              <div
                key={p.key}
                data-testid={`home-plan-${p.key}`}
                className={`relative flex flex-col p-8 border ${
                  featured
                    ? "border-volt bg-[#101400] md:scale-105 shadow-[0_0_50px_rgba(212,255,0,0.15)]"
                    : "border-zinc-800 bg-[#0d0d0d]"
                }`}
              >
                {p.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-volt text-black text-[10px] font-bold tracking-widest px-3 py-1 whitespace-nowrap">
                    {p.badge}
                  </div>
                )}
                <div className="flex items-center gap-2 mb-5">
                  <Icon size={20} className={featured ? "text-volt" : "text-zinc-400"} strokeWidth={2.5} />
                  <span className="text-sm uppercase tracking-widest text-zinc-300">{p.name}</span>
                </div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className={`font-display tracking-tight ${featured ? "text-6xl text-volt" : "text-5xl text-white"}`}>
                    {p.price}
                  </span>
                  <span className="text-xs text-zinc-500">{p.per}</span>
                </div>
                <div className="text-[11px] uppercase tracking-widest text-volt mb-6">{p.duration}</div>
                <ul className="space-y-3 mb-8 flex-grow">
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2 text-xs text-zinc-400">
                      <Check size={14} className={featured ? "text-volt mt-0.5" : "text-zinc-500 mt-0.5"} />
                      {perk}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => (isPaid ? manageBilling() : buy(p.key))}
                  disabled={!!busyKey}
                  data-testid={`buy-${p.key}`}
                  className={`w-full flex items-center justify-center gap-2 py-4 text-sm font-bold uppercase tracking-widest transition-colors duration-100 disabled:opacity-60 ${
                    featured
                      ? "bg-volt text-black hover:bg-volt-hover"
                      : "bg-transparent border border-zinc-700 text-white hover:bg-zinc-800"
                  }`}
                >
                  {busyKey === p.key && <Loader2 size={15} className="animate-spin" />}
                  {isPaid ? "Manage Billing" : p.cta}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-zinc-500 mt-8 text-center leading-relaxed tracking-wide">
          Secure checkout by Stripe. All paid plans renew automatically until canceled.
          Manage recurring billing from your account.
        </p>
      </section>

      {/* CTA FOOTER */}
      <section className="border-t border-zinc-900">
        <div className="max-w-5xl mx-auto px-6 py-20 text-center">
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-6">READY TO HEAR IT?</h2>
          <button
            onClick={() => navigate("/studio")}
            data-testid="home-footer-cta"
            className="inline-flex items-center gap-2 bg-volt text-black px-10 py-4 text-sm font-bold uppercase tracking-widest hover:bg-volt-hover transition-colors duration-100"
          >
            Open The Studio <ArrowRight size={16} strokeWidth={2.5} />
          </button>
        </div>
        <div className="border-t border-zinc-900 px-6 py-6 text-center">
          <p className="text-[11px] text-zinc-600 tracking-wider">
            LOCAL VOICE · 100% client-side text-to-speech · nothing leaves your device
          </p>
        </div>
      </section>
    </div>
  );
}
